import * as React from 'react';
import {connect} from 'react-redux';

import * as Leads from 'nutshell-core/leads';
import {Outcomes} from 'nutshell-core/outcomes';
import type {
    LeadId,
    Lead,
    OutcomeId,
    Outcome,
    OutcomeStatus,
    CompetitorMap,
    CompetitorMapId,
    SelectComponentValue,
} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Select} from 'shells/select/select';
import {SelectableListMultiple} from 'shells/selectable-list/selectable-list';

import type {NutshellState} from '../../../../store';

import styles from './confirmation-form-lose-lead.module.css';

type OwnProps = {
    onCancel: () => any;
    onSubmit: (
        leadId: string,
        outcomeId: OutcomeId,
        data?: {
            competitorMaps: CompetitorMapId[];
        }
    ) => any;
    onClearError: () => any;
    errorMessage?: string;
    isCancelling?: boolean;
    leadId: string;
};

type StateProps = {
    outcomes: Outcome[];
    lead: Lead;
};

type DispatchProps = {
    fetchLead: (leadId: LeadId) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

const mapStateToProps = function (state: NutshellState, ownProps: OwnProps): StateProps {
    const status: OutcomeStatus = ownProps.isCancelling ? 'cancelled' : 'lost';

    return {
        // @ts-expect-error
        lead: Leads.getLead(state, ownProps.leadId),
        outcomes: Outcomes.getOutcomesForStatus(status)(state),
    };
};

// @ts-expect-error
const mapDispatchToProps = function (dispatch): DispatchProps {
    return {
        fetchLead: (leadId) => {
            return dispatch(Leads.fetchLeadById(leadId));
        },
    };
};

/*
Reusable lead lose form component that allows the user to select why the lead
was lost or cancelled, as well as to which competitors
(if the lead has competitors associated with it and is being lost)

Expects just a leadId, as it'll fetch the full lead model upon mounting to get
the related products.
 */
export class ConfirmationFormLoseLeadComponent extends React.PureComponent<
    Props,
    {
        competitorMaps: CompetitorMap[];
        selectedOutcomeId: OutcomeId | null | undefined;
        selectedCompetitorMapIds: CompetitorMapId[];
    }
> {
    constructor(props: Props) {
        super(props);
        this.state = {
            selectedOutcomeId: null,
            selectedCompetitorMapIds: [],
            competitorMaps: props.lead ? props.lead.competitorMaps : [],
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.lead !== this.props.lead) {
            this.setState({
                competitorMaps: nextProps.lead.competitorMaps,
            });
        }
    }

    UNSAFE_componentWillMount() {
        // Don't care about the response, that comes from redux.
        this.props.fetchLead(this.props.leadId);
    }

    render() {
        const selectedOutcome = this.props.outcomes.find((outcome) => {
            return outcome.id === this.state.selectedOutcomeId;
        });

        const outcomePlaceholder = this.props.isCancelling
            ? 'Why is this lead being cancelled?'
            : 'Why was this lead lost?';

        return (
            <div className={styles.container}>
                {this.props.errorMessage ? (
                    <FlashBanner
                        type='error'
                        duration={3000}
                        onHidden={this.props.onClearError}
                        isOverlay={true}
                    >
                        <div>{this.props.errorMessage}</div>
                    </FlashBanner>
                ) : undefined}
                <div className={styles['form-container']}>
                    <div className={styles['form-body']}>
                        <div className={styles['outcomes-container']}>
                            <Select
                                disabledPlaceholderText={outcomePlaceholder}
                                selectValues={this.props.outcomes}
                                value={selectedOutcome}
                                onChange={this.handleOutcomeChange}
                            />
                        </div>
                        {this.props.isCancelling ? undefined : (
                            <div className={styles['competitors-container']}>
                                {/* @ts-expect-error */}
                                <SelectableListMultiple
                                    hasSearch={false}
                                    value={this.state.selectedCompetitorMapIds}
                                    onSelect={this.handleCompetitorMapChange}
                                    collection={this.state.competitorMaps}
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.buttons}>
                        <Button variant='secondary' size='big' onClick={this.props.onCancel}>
                            Cancel
                        </Button>
                        <Button
                            variant={this.props.isCancelling ? 'secondary' : 'danger'}
                            size='big'
                            disabled={!this.state.selectedOutcomeId}
                            onClick={this.handleSubmit}
                        >
                            {this.props.isCancelling ? 'Cancel lead' : 'Lose lead'}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    /**
     * Handles selecting a new outcome - updates our state accordingly
     * with the selected outcome's id.
     *
     * @param  {Object} value     - Outcome model
     * @return {void}
     */
    handleOutcomeChange = (value: SelectComponentValue) => {
        this.setState({selectedOutcomeId: value.id});
    };

    /**
     * Updates the changed productMap's price and quantity, and sets our state
     * with the new updates.
     *
     * @param  {Object[]} competitorMapIds - Array of selected competitorMap Ids
     * @return {void}
     */
    handleCompetitorMapChange = (competitorMapIds: CompetitorMapId[]) => {
        this.setState({
            selectedCompetitorMapIds: competitorMapIds,
        });
    };

    handleSubmit = () => {
        const data = getCompetitorMapDataForLosingLeadPatch(this.state.selectedCompetitorMapIds);

        if (this.state.selectedOutcomeId) {
            this.props.onSubmit(this.props.leadId, this.state.selectedOutcomeId, data);
        }
    };
}

// @ts-expect-error
const connector = connect<Props, OwnProps, _, _, _, _>(mapStateToProps, mapDispatchToProps);
export const ConfirmationFormLoseLead = connector(ConfirmationFormLoseLeadComponent);

/**
 * Helper function to generate the correct shape of data for including in a
 * "win lead" patch request.
 *
 * @param  {string[]} competitorMapIds   - Array of competitor map ids to translate
 * @return {Object}                      - Key'd object that includes transformed payload
 */
function getCompetitorMapDataForLosingLeadPatch(competitorMapIds: CompetitorMapId[]): {
    competitorMaps: CompetitorMapId[];
} {
    return {
        competitorMaps: competitorMapIds,
    };
}
